import { FormControlProps, SystemProps, useMultiStyleConfig } from '@chakra-ui/react';
import { CSSWithMultiValues, RecursiveCSSSelector, RecursivePseudo } from '@chakra-ui/system';
import { omit } from '@chakra-ui/utils';
import { type ChangeEventHandler, useState } from 'react';

import { assignStringBoolean, isDefined } from '../../helpers/utils';
import type { BaseControlComponentProps } from './base-control';

export type ControlBaseType = {
    defaultValue?: any
    value?: any
    onChange?: ChangeEventHandler<any>
};

export type BaseControlState = {
    isUserWarning: boolean
    isInvalid: boolean
    isReadOnly: boolean
    isDisabled: boolean
    isFocused: boolean
    hasValue: boolean
    isWarning: boolean
    isSuccess: boolean
    isHovering: boolean
    isLabelOnTop: boolean
    hasLeftAdornment: boolean
    hasRightAdornment: boolean
};

// This is a webstorm generated type getting typescript TS2742 for not portable type of Component props.
// Automatic inference does not seems safe.
/* eslint-disable max-len */
type Hook<ControlProps extends ControlBaseType> = {
    onOut: (event: any) => void
    labelText: any
    hasValue: boolean
    LeftAdornment: null
    helperText: any
    wrapperStyles: any
    onOver: (event: any) => void
    RightAdornment: null
    styles: Record<string, (CSSWithMultiValues & RecursivePseudo<CSSWithMultiValues>) | (CSSWithMultiValues & RecursiveCSSSelector<CSSWithMultiValues>) | CSSWithMultiValues>
    id: string
    state: BaseControlState
    controlRenderProps: any
    formControlProps: Omit<Pick<Omit<FormControlProps, 'defaultValue' | 'value' | 'onChange'> & {
        id: string
        'data-testid'?: string
        labelText?: string | null
        helperText?: string | null
        isWarning?: boolean
        isSuccess?: boolean
        isUserWarning?: boolean
        RightAdornment?: React.ReactElement | null
        LeftAdornment?: React.ReactElement | null
        renderControl: <ControlProps>(props: ControlProps) => JSX.Element
        assignProps?: <ControlProps>(props: Omit<BaseControlComponentProps<ControlProps>, 'assignProps'>, state: BaseControlState) => any
        assignControlProps?: <ControlProps>(controlProps: ControlProps, props: Omit<BaseControlComponentProps<ControlProps>, 'assignProps'>, state: BaseControlState) => any
        controlProps?: ControlProps
        wrapperStyles?: SystemProps
        hasValuePredicate?: <ControlProps>(value: unknown, props: Omit<BaseControlComponentProps<ControlProps>, 'hasValuePredicate'>) => boolean
        testHover?: boolean
        testFocused?: boolean
        mRef?: React.Ref<any>
    }, keyof BaseControlComponentProps<ControlProps> extends ('isWarning' | 'labelText' | 'testHover' | 'hasValuePredicate' | 'helperText' | 'testFocused' | 'assignProps' | 'id' | 'controlProps' | 'assignControlProps' | 'renderControl' | 'isSuccess') ? never : keyof BaseControlComponentProps<ControlProps>>, string>
    renderControl: (props: ControlProps) => JSX.Element
};
/* eslint-enable max-len */

export function useBaseControlTheme<ControlProps extends ControlBaseType> (
    props: BaseControlComponentProps<ControlProps>,
    ref: any
): Hook<ControlProps> {
    const {
        id,
        labelText = null,
        helperText = null,
        controlProps = {
            value: undefined,
            defaultValue: undefined
        } as ControlProps,
        hasValuePredicate = defaultHasVisibleValuePredicate,
        isSuccess = false,
        isWarning = false,
        renderControl,
        assignProps = noop,
        assignControlProps = noop,
        testFocused = false,
        testHover = false,
        ...restProps
    } = props;
    const [isHovering, setIsHovering] = useState<boolean>(testHover);
    const [isFocused, setIsFocused] = useState<boolean>(testFocused);
    const [hasValue, setHasValue] = useState<boolean>(
        hasValuePredicate(
            controlProps?.value || controlProps?.defaultValue || '',
            props
        )
    );
    const isReadOnly = assignStringBoolean(restProps.isReadOnly);
    const state: BaseControlState = {
        isUserWarning: assignStringBoolean(restProps.isUserWarning),
        isInvalid: assignStringBoolean(restProps.isInvalid),
        isDisabled: assignStringBoolean(restProps.isDisabled),
        isLabelOnTop: hasValue || isFocused && !isReadOnly,
        isReadOnly,
        isFocused,
        hasValue,
        isWarning,
        isSuccess,
        isHovering,
        hasLeftAdornment: isDefined(props.LeftAdornment),
        hasRightAdornment: isDefined(props.RightAdornment)
    };
    const formControlInternalProps = assignProps(props, state);
    const LeftAdornment = formControlInternalProps.LeftAdornment || null;
    const RightAdornment = formControlInternalProps.RightAdornment || null;

    state.hasLeftAdornment = isDefined(formControlInternalProps.LeftAdornment);
    state.hasRightAdornment = isDefined(formControlInternalProps.RightAdornment);

    const styles = useMultiStyleConfig('BaseControl', state);
    const handleChange = (event: any) => {
        setHasValue(hasValuePredicate(event.target.value, props));
        props.controlProps?.onChange && props.controlProps.onChange(event);
    };
    const handleFocus = (event: any) => {
        setIsFocused(true);
        props.onFocus && props.onFocus(event);
    };
    const handleBlur = (event: any) => {
        setIsFocused(false);
        props.onBlur && props.onBlur(event);
    };
    const handleOver = (event: any) => {
        setIsHovering(true);
        props.onMouseOver && props.onMouseOver(event);
    };
    const handleOut = (event: any) => {
        setIsHovering(false);
        props.onMouseOut && props.onMouseOut(event);
    };
    const {
        // manually apply styled-system props could be done with mergeThemeOverride maybe
        _focus,
        _hover,
        _disabled,
        ...baseStyles
        // @ts-ignore Chakra types don't support this abstraction :/
    } = styles.Wrapper as any;
    const wrapperStyles = {
        ...baseStyles,
        ...isFocused ? _focus : null,
        ...props.isDisabled ? _disabled : null,
        ...props.wrapperStyles
    };
    const controlRenderProps = assignControlProps({
        ...controlProps,
        onFocus: handleFocus,
        onBlur: handleBlur,
        onChange: handleChange,
        children: restProps.children,
        ref
    }, formControlInternalProps, state);
    // Manual omit `shouldForwardProp` for properties not to pass to the Chakra
    // FormControl component, otherwise they end up in the DOM :/
    const formControlProps = omit(restProps, ['isUserWarning', 'LeftAdornment', 'RightAdornment']);

    return {
        id,
        hasValue,
        labelText,
        helperText,
        state,
        styles,
        wrapperStyles,
        controlRenderProps,
        formControlProps,
        onOver: handleOver,
        onOut: handleOut,
        renderControl,
        LeftAdornment,
        RightAdornment
    };
}

function noop (props: any, state: any) {
    // Intentional no operation
    return props;
}

function defaultHasVisibleValuePredicate (value: unknown): boolean {
    if (typeof value !== 'string') {
        return false;
    }

    return value.length > 0;
}
